import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentResult({ kind }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState(kind === "success" ? "checking" : "cancelled");
  const tries = useRef(0);

  useEffect(() => {
    if (kind !== "success") return;
    const sid = searchParams.get("session_id");
    if (!sid) { setState("error"); return; }
    const timer = setInterval(async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clearInterval(timer);
        }
      } catch (e) {}
      if (tries.current > 15) {
        setState("pending");
        clearInterval(timer);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [kind, searchParams]);

  const backTarget = () => {
    const bid = localStorage.getItem("pending_booking");
    return bid ? `/bookings/${bid}` : "/bookings";
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="payment-result-page">
      <div className="w-full max-w-md border border-border bg-white p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand" />
            <h1 className="mt-4 font-heading text-xl font-bold" data-testid="payment-status-title">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">We're verifying the transaction with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-brand" />
            <h1 className="mt-4 font-heading text-xl font-bold text-brand" data-testid="payment-status-title">Deposit received</h1>
            <p className="mt-2 text-sm text-muted-foreground">The booking ledger has been updated and the deposit recorded.</p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-4 font-heading text-xl font-bold" data-testid="payment-status-title">Payment processing</h1>
            <p className="mt-2 text-sm text-muted-foreground">Stripe is still confirming this payment. The ledger will update automatically.</p>
          </>
        )}
        {(state === "cancelled" || state === "error") && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-red-600" />
            <h1 className="mt-4 font-heading text-xl font-bold text-red-600" data-testid="payment-status-title">
              {state === "cancelled" ? "Payment cancelled" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">No charge was made. You can retry the deposit from the booking page.</p>
          </>
        )}
        <Button data-testid="payment-back-button" onClick={() => navigate(backTarget())} className="mt-6 w-full rounded-md bg-ink hover:bg-ink-light">
          Back to booking
        </Button>
      </div>
    </div>
  );
}
